import { useState } from 'react';

import { ChatUserMessage, ChatMode } from '@/explore/types';
import { IconButton } from '@/components/button';
import { MessageWithUrlChips } from '@/components/message-with-url-chips';

import { isUserQuestionMessage } from '@/core/cell';

import { UserMessageInput } from './user-message-input';

import commonStyles from '../chat-cell.module.scss';
import styles from './initial-message.module.scss';

interface InitialMessageProps {
  message: ChatUserMessage;
  initialValue?: string;
  editable?: boolean;
  disabled?: boolean;
  onChange: (message: ChatUserMessage) => void;
  onDiscard: () => void;
}

export const InitialMessage = ({
  message,
  initialValue,
  editable = false,
  disabled = false,
  onChange,
  onDiscard,
}: InitialMessageProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = (value: string, sources: string[], mode: ChatMode) => {
    setIsEditing(false);

    if (isUserQuestionMessage(message)) {
      return onChange({ ...message, message: value, sources, research: mode === 'research' });
    }

    return onChange({ ...message, message: value });
  };

  const label = message.type === 'initial_user_prompt' ? 'Question' : 'Follow-up';

  return (
    <div className={(commonStyles.questionRow, styles.initialMessage)}>
      <div className={styles.label}>{label}</div>
      <div>
        {isEditing || (editable && !disabled) ? (
          <UserMessageInput
            initialValue={editable ? (initialValue ?? message.message) : message.message}
            sources={isUserQuestionMessage(message) ? message.sources : undefined}
            mode={
              isUserQuestionMessage(message) && message.research === true ? 'research' : undefined
            }
            placeholder="Edit question"
            onSubmit={handleSubmit}
            onCancel={() => setIsEditing(false)}
            primaryActions={(value, sources, mode) => [
              {
                label: 'Send',
                disabled: value.length === 0,
                onClick: () => handleSubmit(value, sources ?? [], mode),
              },
            ]}
            className={styles.input}
            secondaryActions={[
              !isEditing && editable
                ? {
                    label: 'Discard (Esc)',
                    onClick: (e) => {
                      e.stopPropagation();
                      onDiscard();
                    },
                  }
                : {
                    label: 'Cancel (Esc)',
                    onClick: () => setIsEditing(false),
                  },
            ]}
            autoFocus
          />
        ) : (
          <div className={commonStyles.response} onClick={() => setIsEditing(true)}>
            <MessageWithUrlChips message={message.message} />
            <IconButton
              icon="Edit2"
              iconSize="small"
              className={commonStyles.responseEditBtn}
              disabled={disabled}
              onClick={() => setIsEditing(true)}
            />
          </div>
        )}
      </div>
    </div>
  );
};
